import React, { useEffect, useState } from 'react'
import { Box, Button, Divider, IconButton, MenuItem, Select, Stack, Tooltip } from '@mui/material'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'
import { Mark } from '@tiptap/core'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TextAlign from '@tiptap/extension-text-align'
import PlaceholderNode, { toEditorHtml, fromEditorHtml } from './PlaceholderNode'

// Inline Underline mark — defined here instead of pulling in
// @tiptap/extension-underline so no new npm dependency is needed.
const Underline = Mark.create({
    name: 'underline',
    parseHTML() {
        return [
            { tag: 'u' },
            { style: 'text-decoration', getAttrs: (v) => /(^|\s)underline(\s|$)/.test(v) && null },
        ]
    },
    renderHTML() { return ['u', 0] },
    addCommands() {
        return {
            toggleUnderline: () => ({ commands }) => commands.toggleMark(this.name),
        }
    },
})

// Tiptap's default TableCell/TableHeader PARSE colwidth from input HTML but
// don't WRITE it back on serialisation — so user drag-resizes are silently
// lost when we call editor.getHTML(). These extensions add the missing
// renderHTML for the colwidth attribute, and also emit `style="width: Npx"`
// alongside so xhtml2pdf has something to read at PDF render time.
function addColwidthRender(baseExt) {
    return baseExt.extend({
        addAttributes() {
            const parent = this.parent?.() || {}
            return {
                ...parent,
                colwidth: {
                    default: null,
                    parseHTML: (el) => {
                        const cw = el.getAttribute('colwidth')
                        return cw ? cw.split(',').map((n) => parseInt(n, 10)) : null
                    },
                    renderHTML: (attrs) => {
                        if (!attrs.colwidth || !attrs.colwidth.length) return {}
                        const widths = attrs.colwidth
                        // For merged cells (colspan>1), `widths` holds one entry per
                        // spanned column. The cell's CSS width must be the SUM of
                        // those entries — using just widths[0] here was the cause
                        // of merged cells rendering with a single-column width.
                        const totalPx = widths.reduce((a, b) => a + (Number(b) || 0), 0)
                        return {
                            colwidth: widths.join(','),
                            style: `width: ${totalPx}px`,
                        }
                    },
                },
            }
        },
    })
}
const ResizableTableCell = addColwidthRender(TableCell)
const ResizableTableHeader = addColwidthRender(TableHeader)

/**
 * Word-style WYSIWYG editor for the AI-extracted consent form HTML.
 *
 * Built so a non-technical clinical user can fix anything the AI got wrong:
 * click a cell to edit text, use the toolbar to add/remove/merge rows and
 * columns, and pick patient/admission fields from a dropdown chip rendered
 * inline (NEVER showing raw {{key}} text).
 */
export default function ConsentHtmlEditor({ value, onChange, disabled, placeholderOptions }) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
            Table.configure({ resizable: true, HTMLAttributes: { border: '1' } }),
            TableRow,
            ResizableTableHeader,
            ResizableTableCell,
            TextAlign.configure({
                types: ['heading', 'paragraph', 'tableCell', 'tableHeader'],
                alignments: ['left', 'center', 'right', 'justify'],
            }),
            Underline,
            PlaceholderNode.configure({ placeholderOptions: placeholderOptions || [] }),
        ],
        content: toEditorHtml(value || '<p>Empty form</p>'),
        editable: !disabled,
        onUpdate: ({ editor: e }) => {
            if (onChange) onChange(fromEditorHtml(e.getHTML()))
        },
    }, [disabled, JSON.stringify((placeholderOptions || []).map(o => o.value))])

    // Push external updates (e.g. after re-extract) into the editor
    useEffect(() => {
        if (!editor) return;
        const currentCanonical = fromEditorHtml(editor.getHTML())
        if ((value || '') !== currentCanonical) {
            editor.commands.setContent(toEditorHtml(value || '<p>Empty form</p>'), false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    const [pendingPlaceholder, setPendingPlaceholder] = useState('')
    const insertPlaceholder = (key) => {
        if (!editor || !key) return;
        editor.chain().focus().insertContent({
            type: 'placeholder',
            attrs: { dataKey: key },
        }).run()
        setPendingPlaceholder('')
    }

    if (!editor) return null;

    const inTable = editor.isActive('table')
    const tableBtn = (label, action, enabled = inTable) => (
        <Tooltip title={label} key={label}>
            <span>
                <Button size="small" variant="outlined" disabled={!enabled || disabled}
                    onClick={() => editor.chain().focus()[action]().run()}
                    sx={{ minWidth: 0, padding: '2px 8px', fontSize: '0.7vw', textTransform: 'none' }}>
                    {label}
                </Button>
            </span>
        </Tooltip>
    )

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Toolbar */}
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap"
                sx={{ p: 0.5, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa', gap: 0.5 }}>
                <Tooltip title="Bold">
                    <IconButton size="small" disabled={disabled}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        sx={{ backgroundColor: editor.isActive('bold') ? '#e0e0e0' : 'transparent' }}>
                        <FormatBoldIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Italic">
                    <IconButton size="small" disabled={disabled}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        sx={{ backgroundColor: editor.isActive('italic') ? '#e0e0e0' : 'transparent' }}>
                        <FormatItalicIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Underline">
                    <IconButton size="small" disabled={disabled}
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        sx={{ backgroundColor: editor.isActive('underline') ? '#e0e0e0' : 'transparent' }}>
                        <FormatUnderlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Align left">
                    <IconButton size="small" disabled={disabled}
                        onClick={() => editor.chain().focus().setTextAlign('left').run()}
                        sx={{ backgroundColor: editor.isActive({ textAlign: 'left' }) ? '#e0e0e0' : 'transparent' }}>
                        <FormatAlignLeftIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Align center">
                    <IconButton size="small" disabled={disabled}
                        onClick={() => editor.chain().focus().setTextAlign('center').run()}
                        sx={{ backgroundColor: editor.isActive({ textAlign: 'center' }) ? '#e0e0e0' : 'transparent' }}>
                        <FormatAlignCenterIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Align right">
                    <IconButton size="small" disabled={disabled}
                        onClick={() => editor.chain().focus().setTextAlign('right').run()}
                        sx={{ backgroundColor: editor.isActive({ textAlign: 'right' }) ? '#e0e0e0' : 'transparent' }}>
                        <FormatAlignRightIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Justify">
                    <IconButton size="small" disabled={disabled}
                        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                        sx={{ backgroundColor: editor.isActive({ textAlign: 'justify' }) ? '#e0e0e0' : 'transparent' }}>
                        <FormatAlignJustifyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Undo">
                    <IconButton size="small" disabled={disabled || !editor.can().undo()}
                        onClick={() => editor.chain().focus().undo().run()}>
                        <UndoIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Redo">
                    <IconButton size="small" disabled={disabled || !editor.can().redo()}
                        onClick={() => editor.chain().focus().redo().run()}>
                        <RedoIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Select size="small" displayEmpty value={pendingPlaceholder}
                    onChange={(e) => insertPlaceholder(e.target.value)}
                    disabled={disabled}
                    sx={{ fontSize: '0.8vw', minWidth: 180, height: 30 }}>
                    <MenuItem value="" disabled><em>Insert patient field…</em></MenuItem>
                    {(placeholderOptions || []).filter(o => o.value).map(o => (
                        <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.8vw' }}>
                            {o.label}
                        </MenuItem>
                    ))}
                </Select>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                {/* Table operations — enabled only when caret is inside a table */}
                {tableBtn('+ Row above', 'addRowBefore')}
                {tableBtn('+ Row below', 'addRowAfter')}
                {tableBtn('+ Col left', 'addColumnBefore')}
                {tableBtn('+ Col right', 'addColumnAfter')}
                {tableBtn('Delete row', 'deleteRow')}
                {tableBtn('Delete col', 'deleteColumn')}
                <Tooltip title="Drag-select cells first, then click to merge them. Click on a merged cell to split it back.">
                    <span>{tableBtn('Merge cells', 'mergeOrSplit')}</span>
                </Tooltip>
                {tableBtn('Split cell', 'splitCell')}

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Insert a new 2x2 table">
                    <span>
                        <Button size="small" variant="outlined" disabled={disabled}
                            onClick={() => editor.chain().focus().insertTable({ rows: 2, cols: 2, withHeaderRow: false }).run()}
                            sx={{ minWidth: 0, padding: '2px 8px', fontSize: '0.7vw', textTransform: 'none' }}>
                            + Table
                        </Button>
                    </span>
                </Tooltip>
                {tableBtn('Delete table', 'deleteTable')}
            </Stack>

            {/* Editor area */}
            <Box sx={{
                flex: 1,
                overflow: 'auto',
                p: 1.5,
                backgroundColor: '#fff',
                '& .ProseMirror': {
                    outline: 'none',
                    minHeight: '100%',
                    fontSize: '0.85vw',
                    lineHeight: 1.5,
                    color: '#000',
                    // Preserve consecutive spaces typed by the user (HTML normally collapses them).
                    // pre-wrap also wraps long lines at word boundaries, so it doesn't break layout.
                    whiteSpace: 'pre-wrap',
                    '& table': {
                        borderCollapse: 'collapse',
                        width: '100%',
                        margin: '8pt 0',
                        tableLayout: 'fixed',
                    },
                    '& table td, & table th': {
                        border: '1px solid #888',
                        padding: '6px 8px',
                        verticalAlign: 'top',
                        position: 'relative',
                    },
                    '& table .selectedCell::after': {
                        backgroundColor: 'rgba(33, 150, 243, 0.12)',
                        content: '""',
                        left: 0, right: 0, top: 0, bottom: 0,
                        pointerEvents: 'none',
                        position: 'absolute',
                        zIndex: 2,
                    },
                    // Tiptap's column-resize plugin draws a 4px grab handle on
                    // each cell's right edge and adds .resize-cursor to the
                    // editable root while dragging. Without these styles the
                    // user can't see WHERE to grab and gets no cursor feedback.
                    '& table .column-resize-handle': {
                        position: 'absolute',
                        right: '-2px',
                        top: 0,
                        bottom: '-2px',
                        width: '4px',
                        backgroundColor: '#2196f3',
                        cursor: 'col-resize',
                        pointerEvents: 'none',
                    },
                    '&.resize-cursor': {
                        cursor: 'col-resize',
                    },
                    '& h1': { fontSize: '1.4vw', textAlign: 'center', margin: '6pt 0' },
                    '& h2': { fontSize: '1.1vw', margin: '6pt 0' },
                    '& h3': { fontSize: '1vw', margin: '6pt 0' },
                    '& p': { margin: '6pt 0' },
                    '& hr': { border: 0, borderTop: '1px solid #888', margin: '8pt 0' },
                },
            }}>
                <EditorContent editor={editor} />
            </Box>
        </Box>
    )
}
